import { BookingActionTypes } from '../action-types/bookingTypes';
import { BookingActions } from '../actions/bookingTypes';

export interface BookingState {
  category: string;
  categoryTrackingId: string;
  travelers: TravelersType;
  rental?: boolean;
  excessAmount: number;
  residence?: string;
  tripCost?: number;
  geographicalZone?: GeographicalZoneType;
  meansOfTravel?: MeansOfTravelType;
  startDate: string;
  endDate: string;
  bookingConfirmationDate: string;
  policyHolderSalutation: SalutationsType;
  policyHolderFirstName: string;
  policyHolderLastName: string;
  policyHolderCompany: string | null;
  policyHolderSamePerson: boolean;
  policyHolderStreet: string;
  policyHolderPostalCode: string;
  policyHolderCity: string;
  policyHolderCountry: string;
  policyHolderPhone: string;
  policyHolderEmail: string;
  policyHolderEmail2: string;
  hasConfirmedPrecontractualDocuments: boolean;
  policyHolderAccountHolder: string;
  policyHolderIBAN: string;
  persons: Array<Person>;
  paymentMethod: string;
  covidProtection: boolean;
  excess: boolean;
  travelCancellation: boolean;
  hasConfirmedPayment: boolean;
  ergoOrganisationUnit: ErgoOrganisationUnitType;
  displayTripCost?: string;
  displayIndividualTripCost?: Array<string>;
  agent: string;
  hideJV: boolean;
}

const initialState: BookingState = {
  category: '',
  categoryTrackingId: '',
  travelers: { adultsUpTo40: null, adultsUpTo64: null, adultsFrom65: null, children: null, complementary: null },
  rental: undefined,
  excessAmount: 0,
  residence: undefined,
  tripCost: 0,
  geographicalZone: undefined,
  meansOfTravel: undefined,
  startDate: '',
  endDate: '',
  bookingConfirmationDate: '',
  policyHolderSalutation: null,
  policyHolderFirstName: '',
  policyHolderLastName: '',
  policyHolderCompany: null,
  policyHolderSamePerson: false,
  policyHolderStreet: '',
  policyHolderPostalCode: '',
  policyHolderCity: '',
  policyHolderCountry: '',
  policyHolderPhone: '',
  policyHolderEmail: '',
  policyHolderEmail2: '',
  hasConfirmedPrecontractualDocuments: false,
  policyHolderAccountHolder: '',
  policyHolderIBAN: '',
  persons: [],
  paymentMethod: '',
  covidProtection: false,
  excess: false,
  travelCancellation: false,
  hasConfirmedPayment: false,
  ergoOrganisationUnit: {
    organisation1Number: '',
    organisation1Percent: null,
    organisation2Number: '',
    organisation2Percent: null,
    advertisingMaterialKey: '',
    wayOfAccess: '',
    tipster: false,
    productionDST: '',
  },
  displayTripCost: '',
  displayIndividualTripCost: [],
  agent: '',
  hideJV: false,
};

const bookingReducer = (state: BookingState = initialState, action: BookingActions) => {
  switch (action.type) {
    case BookingActionTypes.SET_CATEGORY:
      return {
        ...state,
        category: action.payload,
      };
    case BookingActionTypes.SET_CATEGORY_TRACKING_ID:
      return {
        ...state,
        categoryTrackingId: action.payload,
      };
    case BookingActionTypes.SET_TRAVELERS:
      return {
        ...state,
        travelers: action.payload,
      };
    case BookingActionTypes.SET_RENTAL:
      return {
        ...state,
        rental: action.payload,
      };
    case BookingActionTypes.SET_TRIP_COST:
      return {
        ...state,
        tripCost: action.payload,
      };
    case BookingActionTypes.SET_EXCESS_AMOUNT:
      return {
        ...state,
        excessAmount: action.payload,
      };
    case BookingActionTypes.SET_RESIDENCE:
      return {
        ...state,
        residence: action.payload,
      };
    case BookingActionTypes.SET_GEOGRAPHICAL_ZONE:
      return {
        ...state,
        geographicalZone: action.payload,
      };
    case BookingActionTypes.SET_MEANS_OF_TRAVEL:
      return {
        ...state,
        meansOfTravel: action.payload,
      };
    case BookingActionTypes.SET_START_DATE:
      return {
        ...state,
        startDate: action.payload,
      };
    case BookingActionTypes.SET_END_DATE:
      return {
        ...state,
        endDate: action.payload,
      };
    case BookingActionTypes.SET_BOOKING_CONFIRMATION_DATE:
      return {
        ...state,
        bookingConfirmationDate: action.payload,
      };
    case BookingActionTypes.SET_POLICY_HOLDER_SALUTATION:
      return {
        ...state,
        policyHolderSalutation: action.payload,
      };
    case BookingActionTypes.SET_POLICY_HOLDER_FIRST_NAME:
      return {
        ...state,
        policyHolderFirstName: action.payload,
      };
    case BookingActionTypes.SET_POLICY_HOLDER_LAST_NAME:
      return {
        ...state,
        policyHolderLastName: action.payload,
      };
    case BookingActionTypes.SET_POLICY_HOLDER_COMPANY:
      return {
        ...state,
        policyHolderCompany: action.payload,
      };
    case BookingActionTypes.SET_POLICY_HOLDER_SAME_PERSON:
      return {
        ...state,
        policyHolderSamePerson: action.payload,
      };
    case BookingActionTypes.SET_POLICY_HOLDER_STREET:
      return {
        ...state,
        policyHolderStreet: action.payload,
      };
    case BookingActionTypes.SET_POLICY_HOLDER_POSTAL_CODE:
      return {
        ...state,
        policyHolderPostalCode: action.payload,
      };
    case BookingActionTypes.SET_POLICY_HOLDER_CITY:
      return {
        ...state,
        policyHolderCity: action.payload,
      };
    case BookingActionTypes.SET_POLICY_HOLDER_COUNTRY:
      return {
        ...state,
        policyHolderCountry: action.payload,
      };
    case BookingActionTypes.SET_POLICY_HOLDER_PHONE:
      return {
        ...state,
        policyHolderPhone: action.payload,
      };
    case BookingActionTypes.SET_POLICY_HOLDER_EMAIL:
      return {
        ...state,
        policyHolderEmail: action.payload,
      };
    case BookingActionTypes.SET_POLICY_HOLDER_EMAIL2:
      return {
        ...state,
        policyHolderEmail2: action.payload,
      };
    case BookingActionTypes.SET_HAS_CONFIRMED_PRECONTRACTUAL_DOCUMENTS:
      return {
        ...state,
        hasConfirmedPrecontractualDocuments: action.payload,
      };
    case BookingActionTypes.SET_POLICY_HOLDER_ACCOUNT_HOLDER:
      return {
        ...state,
        policyHolderAccountHolder: action.payload,
      };
    case BookingActionTypes.SET_POLICY_HOLDER_IBAN:
      return {
        ...state,
        policyHolderIBAN: action.payload,
      };
    case BookingActionTypes.SET_PERSONS:
      return {
        ...state,
        persons: action.payload,
      };
    case BookingActionTypes.SET_PAYMENT_METHOD:
      return {
        ...state,
        paymentMethod: action.payload,
      };
    case BookingActionTypes.SET_COVID_PROTECTION:
      return {
        ...state,
        covidProtection: action.payload,
      };
    case BookingActionTypes.SET_EXCESS:
      return {
        ...state,
        excess: action.payload,
      };
    case BookingActionTypes.SET_HIDE_JV:
      return {
        ...state,
        hideJV: action.payload,
      };
    case BookingActionTypes.SET_TRAVEL_CANCELLATION:
      return {
        ...state,
        travelCancellation: action.payload,
      };
    case BookingActionTypes.SET_HAS_CONFIRMED_PAYMENT:
      return {
        ...state,
        hasConfirmedPayment: action.payload,
      };
    case BookingActionTypes.SET_ERGO_ORGANISATION_UNIT:
      return {
        ...state,
        ergoOrganisationUnit: action.payload,
      };
    case BookingActionTypes.SET_OENR1:
      return {
        ...state,
        ergoOrganisationUnit: {
          ...state.ergoOrganisationUnit,
          organisation1Number: action.payload,
        },
      };
    case BookingActionTypes.SET_WAY_OF_ACCESS:
      return {
        ...state,
        ergoOrganisationUnit: {
          ...state.ergoOrganisationUnit,
          wayOfAccess: action.payload,
        },
      }; 
     case BookingActionTypes.SET_DISPLAY_TRIP_COST:
      return {
        ...state,
        displayTripCost: action.payload,
      };
    case BookingActionTypes.SET_DISPLAY_INDIVIDUAL_TRIP_COST:
      return {
        ...state,
        displayIndividualTripCost: action.payload,
      };
    case BookingActionTypes.SET_AGENT:
      return {
        ...state,
        agent: action.payload,
      };
    default:
      return state;
  }
};

export default bookingReducer;
